import React from "react";
import { getIn, useFormikContext } from "formik";
import toast from "react-hot-toast";
import styles from "./VerifyIdButton.module.scss";
import Spinner from "../Loaders/Spinner";
import IdvStatusBadge, {
  IDV_FAILED,
  IDV_PASSED,
  IDV_PENDING_REVIEW,
} from "../IdvStatusBadge/IdvStatusBadge";

const PlaidBoltIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20.5491 11.7617L10.0491 23.0117C9.93784 23.1305 9.79097 23.2098 9.63065 23.2377C9.47034 23.2657 9.30528 23.2407 9.16039 23.1667C9.0155 23.0926 8.89863 22.9734 8.82743 22.827C8.75623 22.6807 8.73455 22.5152 8.76568 22.3555L10.14 15.4808L4.73724 13.452C4.62121 13.4086 4.51773 13.3372 4.43606 13.244C4.35439 13.1508 4.29706 13.0389 4.26921 12.9182C4.24135 12.7975 4.24383 12.6718 4.27642 12.5522C4.30902 12.4327 4.37071 12.3231 4.45599 12.2333L14.956 0.983274C15.0673 0.864526 15.2141 0.785191 15.3744 0.75724C15.5348 0.729289 15.6998 0.75424 15.8447 0.828326C15.9896 0.902412 16.1065 1.02161 16.1777 1.16794C16.2489 1.31428 16.2705 1.47979 16.2394 1.63952L14.8613 8.52171L20.2641 10.5476C20.3793 10.5913 20.4819 10.6627 20.563 10.7555C20.644 10.8483 20.7009 10.9596 20.7287 11.0796C20.7565 11.1996 20.7544 11.3246 20.7224 11.4436C20.6904 11.5626 20.6296 11.6718 20.5454 11.7617H20.5491Z"
      fill="white"
    />
  </svg>
);

const LABELS = {
  [IDV_PASSED]: "ID verified",
  [IDV_PENDING_REVIEW]: "ID under review",
  [IDV_FAILED]: "Retry ID verification",
};

/**
 * "Verify ID with Plaid" for one owner, inside the application's Formik form.
 * `fields` maps first_name / last_name / email to that owner's form paths;
 * they are checked with the form's own rules before a session starts, so an
 * owner never verifies under a name the form would then make them change.
 * `idv` comes from usePlaidIdv.
 */
const VerifyIdButton = ({ idv, fields, loadingKey }) => {
  const { values, validateForm, setFieldTouched } = useFormikContext();

  const identity = {
    first_name: getIn(values, fields.first_name),
    last_name: getIn(values, fields.last_name),
    email: getIn(values, fields.email),
  };
  const { outcome, reportedOutcome } = idv.getState(identity);
  const isLoading = idv.loadingKey === loadingKey;

  // A failed check can be retried; a passed or in-review one cannot — unless
  // Plaid told the merchant's browser it failed. That report is only trusted
  // this far: forging it buys nothing but another attempt, and never undoes a
  // trusted pass. Mirrors PowerappPlaidIdv::canRestart.
  const canRetry =
    outcome !== IDV_PASSED &&
    (outcome === IDV_FAILED || reportedOutcome === IDV_FAILED);
  const isLocked =
    !canRetry && (outcome === IDV_PASSED || outcome === IDV_PENDING_REVIEW);

  const handleClick = () => {
    if (isLocked || idv.loadingKey) {
      return;
    }

    // Validation runs inside start(), after the session is reserved, so a
    // double click cannot slip a second session past it.
    idv.start(identity, loadingKey, async () => {
      const errors = await validateForm();
      // Blank fields count too: the agent flow's schema skips blank values
      const invalid = Object.values(fields).filter(
        (path) =>
          getIn(errors, path) || !String(getIn(values, path) ?? "").trim(),
      );
      if (invalid.length > 0) {
        invalid.forEach((path) => setFieldTouched(path, true, false));
        toast.error("Check this owner's name and email before verifying ID.");
        return false;
      }
      return true;
    });
  };

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={`${styles.button} ${idv.isKurvDomain ? styles.kurv : ""} ${
          outcome === IDV_PASSED ? styles.connected : ""
        } ${isLocked && outcome === IDV_PENDING_REVIEW ? styles.underReview : ""} ${
          isLoading ? styles.loading : ""
        }`}
        disabled={isLocked || isLoading}
        onClick={handleClick}
      >
        <span className={styles.text}>
          <PlaidBoltIcon />
          {(canRetry ? LABELS[IDV_FAILED] : LABELS[outcome]) ||
            "Verify ID with Plaid"}
        </span>
        {isLoading && (
          <Spinner size={24} color="#FFFFFF" secondaryColor="#FFFFFF" />
        )}
      </button>
      {outcome === IDV_FAILED && <IdvStatusBadge outcome={IDV_FAILED} />}
    </div>
  );
};

export default VerifyIdButton;
